import {
    AfterContentInit,
    ChangeDetectorRef,
    ContentChild,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Renderer2
} from '@angular/core';
import { NgControl, NgForm } from '@angular/forms';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import { ICalendarOptions } from '@df/ui/form/calendar/calendar2.component.def';
import { IcmsIcons, IcmsIconsId } from '@icms/ui/icons/generated/icms-icons';
import defaults from 'lodash-es/defaults';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import isDate from 'lodash-es/isDate';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, Subscription } from 'rxjs';
import { dateToValue } from './../../util/format/date-to-value';
import { ICmsFormFieldModel } from './icms-form-field.model';

export interface IFormFieldOptions {
    // if provided then we act as date field
    date?: ICalendarOptions | true;
}
@Directive({
    standalone: false
})
export abstract class ICmsFormFieldAbstractComponent implements AfterContentInit, OnDestroy, OnChanges, OnInit {
    protected type?: string | null;

    @Input()
    label: string = '';

    @Input('sideIcon')
    _sideIcon?: IcmsIcons | IcmsIconsId;

    @Input()
    hideSideIcon = false;

    @Input()
    busy = false;

    @Input()
    helper?: string;

    @Input()
    error?: string;

    @Input()
    errors: { [error: string]: string } = {};

    @Input('type')
    _type?: string;

    @Output()
    private sideIconClick = new EventEmitter<void>();

    private _ngControl?: NgControl;

    @ContentChild(NgControl, { static: false })
    set ngControl(ngControl: NgControl) {
        this._ngControl = this.formFieldModel?.item?.ngControl || ngControl;
    }

    get ngControl(): NgControl | undefined {
        return this._ngControl;
    }

    private _inputRef?: ElementRef;

    @ContentChild(NgControl, { static: false, read: ElementRef })
    set inputRef(ref: ElementRef) {
        this._inputRef = this.formFieldModel?.item?.inputRef || ref;
        const element = this._inputRef?.nativeElement as HTMLElement;
        const type = this._type || element?.tagName.toLowerCase();
        if (this.type) {
            this.renderer2.removeClass(this.elementRef?.nativeElement, `_${this.type}`);
        }
        this.type = type ? (type === 'input' ? element.getAttribute('type') : type) : undefined;
        if (this.type) {
            this.renderer2.addClass(this.elementRef?.nativeElement, `_${this.type}`);
        }
        // kill calendar opener for date field (in firefox`)
        if (['date', 'time'].includes(String(this.type))) {
            (this._inputRef?.nativeElement as HTMLInputElement).addEventListener('click', e => {
                e.preventDefault();
            });
        }
    }

    get inputRef(): ElementRef | undefined {
        return this._inputRef || this.formFieldModel?.item?.inputRef;
    }

    get isRequired(): boolean {
        if (this.ngControl?.validator) {
            return !!this.ngControl?.validator(this.ngControl?.control!)?.['required'];
        }
        return false;
    }

    get isDisabled(): boolean {
        return this.ngControl?.disabled || false;
    }

    /**
     * If there was attempt to submit the form
     * only then we show validation errors
     */
    @HostBinding('class._submitted')
    get submitted(): boolean {
        return !!this.ngForm?.submitAttempt;
    }

    // responsible for _invalid class but cannot be hostBound cause of change detection
    private get isInvalid(): boolean {
        return !!this.ngControl?.errors;
    }

    // responsible for _isEmpty class but cannot be hostBound cause of change detection
    private get isEmpty(): boolean {
        return !!(this.ngControl && (this.ngControl.value === 0 ? false : !this.ngControl.value));
    }

    /**
     * Subscriptions list
     */
    private subscriptions = new Subscription();

    private initiated = false;

    /**
     * Exapnsion panel state
     * used with some elements like date field
     */
    protected expandState = new BehaviorSubject(false);

    /**
     * Last state of calendar options
     */
    private lastCalendarOptions?: ICalendarOptions;

    private logger = inject(Logger);
    private renderer2 = inject(Renderer2);
    private elementRef = inject(ElementRef);
    private formFieldModel = inject(ICmsFormFieldModel);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private timeoutService = inject(TimeoutService);
    private ngForm? = inject(NgForm, { optional: true });
    
    /**
     * Dependencies
     */
    constructor() {
        this.subscriptions.add(
            this.formFieldModel.itemsChange.subscribe(item => {
                if (item) {
                    this.inputRef = item.inputRef;
                    this.ngControl = item.ngControl;
                    this.onNgControllChange();
                }
            })
        );
        this.subscriptions.add(
            this.formFieldModel?.paramsChange.subscribe(() => {
                this.detectChangesThrottled();
            })
        );
        if (this.ngForm) {
            this.subscriptions.add(this.ngForm.ngSubmit.subscribe(() => this.detectChanges()));
        }
    }

    ngAfterContentInit(): void {
        if (!this.ngControl) {
            this.logger.error(`Form component is missing ngModel inside (${this.label})`, this);
        } else {
            this.subscriptions.add(
                this.ngControl.valueChanges?.subscribe(change => {
                    this.detectChangesThrottled();
                })
            );
        }
        this.onNgControllChange();
    }

    ngOnInit() {
        this.subscriptions.add(this.expandState.subscribe(() => this.detectChanges()));
        this.prepareErrors();
        this.updateHostClasses();
    }

    ngOnChanges(changes) {
        if (changes.errors) {
            this.prepareErrors();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Retrieves error message
     */
    get validationMsg(): string | undefined {
        const errorCodes = this.getErrors();

        if (errorCodes) {
            const code = find(errorCodes, code => !!this.errors[code]);

            return (code && this.errors[code]) || NOTICES[errorCodes[0]] || errorCodes[0]; // || NOTICES.INVALIDFIELD;
        }

        return undefined;
    }

    get sideIcon(): IcmsIcons | IcmsIconsId | undefined {
        if (this.hideSideIcon || this.busy) {
            return undefined;
        }
        if (this._sideIcon) {
            return this._sideIcon;
        }
        switch (this.type) {
            case 'time':
                return IcmsIcons.Time;
            case 'date':
                return IcmsIcons.Calendar;
            case 'select':
                return IcmsIcons.ChevronDown;
            case 'text':
            case 'email':
            case 'textarea':
            case 'number':
                // case 'password': // you can uncomment if you move side text
                return (
                    !this.isDisabled &&
                    !this.isInvalid &&
                    this.ngControl &&
                    this.initiated &&
                    (this.value || this.value === 0) &&
                    IcmsIcons.Tick
                );
            default:
                return undefined;
        }
    }

    get sideText(): string | undefined {
        if (this.busy) {
            return undefined;
        }
        switch (this.type) {
            case 'password':
                return this.inputRef?.nativeElement.type === 'password' ? 'Show' : 'Hide';
            default:
                return undefined;
        }
    }

    get isLoaderVisible(): boolean {
        return this.busy && !this.showsLoaderOnLabel();
    }

    get isLabelBusy(): boolean {
        return this.busy && this.showsLoaderOnLabel();
    }

    /**
     * Validation is shown after submission, but also when there are other errors then field not being filled1
     */
    get isValidationVisible(): boolean {
        const errors = this.getErrors();
        return !!(errors && (this.submitted || errors.find(i => i !== 'required')));
    }

    protected sideAction() {
        if (!this.hasSideAction) {
            return;
        }
        switch (this.type) {
            case 'date':
            case 'time':
                return this.expandState.next(!this.expandState.value);
            case 'password':
                return this.toggleInputVisibility();
        }

        if (this._sideIcon) {
            this.sideIconClick.emit();
        }
    }

    protected get hasSideAction() {
        return (!this.isDisabled && this._sideIcon) || ['date', 'time', 'password'].includes(String(this.type));
    }

    private onNgControllChange() {
        if (!this.initiated && this.ngControl) {
            this.timeoutService.setTimeout(() => {
                this.initiated = true;
                this.detectChanges();
            }, 100);
            this.updateHostClasses();
            this.renderer2.addClass(this.elementRef.nativeElement, '_initiated');
        }
    }

    /**
     * Retrieves error codes from control
     */
    private getErrors(): string[] | undefined {
        const errors = this.ngControl?.errors || undefined;
        return errors && Object.keys(errors);
    }

    /**
     * Loader may appear as icon or as label busy state
     * the first does not work with checkboxes or radio buttons
     */
    private showsLoaderOnLabel(): boolean {
        if (this.type) {
            return ['checkbox', 'radio'].includes(this.type);
        }
        return false;
    }

    private prepareErrors() {
        this.errors = defaults(this.errors || {}, {
            email: NOTICES['INVLIDEMAILFIELD'],
            required: NOTICES['REQUIREDFIELD'],
            validateEqual: NOTICES['NOTMATCHED'],
            emojiDetected: NOTICES['EMOJIDETECTED']
        });
    }

    private toggleInputVisibility() {
        const input = this.inputRef?.nativeElement;
        if (input) {
            input.type = input.type === 'password' ? 'text' : 'password';
            input.type === 'password';
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private detectChangesThrottled = this.timeoutService.throttle(() => this.detectChanges(), 200, { leading: false, trailing: true });

    /**
     * Change detection
     */
    private detectChanges() {
        if (this.initiated) {
            this.updateHostClasses();
            this.changeDetectorRef.detectChanges();
        }
    }

    private updateHostClasses() {
        each(
            {
                _isEmpty: this.isEmpty,
                _invalid: this.isInvalid,
                _required: this.isRequired,
                _disabled: this.isDisabled
            },
            (value, className) => {
                value
                    ? this.renderer2.addClass(this.elementRef.nativeElement, className)
                    : this.renderer2.removeClass(this.elementRef.nativeElement, className);
            }
        );
    }

    /**
     * Retrieves options to pass to calendar
     */
    get calendarOptions(): ICalendarOptions | undefined {
        const options = {
            min: this._inputRef?.nativeElement?.getAttribute('min'),
            max: this._inputRef?.nativeElement?.getAttribute('max')
        };
        if (!isEqual(this.lastCalendarOptions, options)) {
            this.lastCalendarOptions = options;
        }
        return this.lastCalendarOptions;
    }

    /**
     * Value getter
     * needed in some cases
     */
    get value() {
        return this.ngControl?.value;
    }

    /**
     * Value setter
     * needed for calendar, normalizes input
     */
    set value(value) {
        if (isDate(value)) {
            value = dateToValue(value);
        }
        if (this.ngControl?.control) {
            if (value) {
                this.ngControl?.control.markAsDirty();
            }

            if (value && this.ngControl.control.value !== value) {
                this.ngControl.control.setValue(value);
            }
        }
    }

    /**
     * In order to prevent calendar from doint its magic while we try to put date manualy
     */
    protected get inDropdownValue() {
        return this.expandState.value || this.type !== 'date' ? this.value : undefined;
    }

    protected set inDropdownValue(value: any) {
        if (this.expandState.value || this.type !== 'date') {
            this.value = value;
        }
    }

    protected closeDropdown() {
        this.expandState.next(false);
    }
}
